import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Download, XCircle, Loader2, GitMerge, ShieldCheck } from "lucide-react";
import { PageHeader, StatusPill, EmptyState, ErrorBox } from "@/components/common";
import { PaginatedTable } from "@/components/PaginatedTable";
import { downloadBlob, rawFetch } from "@/lib/api/client";
import { useApp } from "@/lib/store";
import { fmtDate, shortId } from "@/lib/utils";

interface Audit {
  id: string; actor: string; actor_id?: string;
  action: string; resource: string; resource_kind?: string; resource_id?: string;
  created_at: string;
}
interface Cred { id: string; provider: string; alias: string; status: string }
interface CostEvent {
  id: string; run_id?: string; actor: string;
  provider: string; model: string;
  input_tokens: number; output_tokens: number;
  cost_usd: string | number; latency_ms?: number;
  tags?: Record<string, unknown>;
  created_at: string;
}
interface LineageEdge {
  id: string; src_kind: string; src_id: string;
  dst_kind: string; dst_id: string;
  op: string; run_id?: string; metadata?: Record<string, unknown>;
  created_at: string;
}
interface ConfidenceScore {
  id: string; target_kind: string; target_id: string;
  retrieval_confidence?: number | null;
  ontology_confidence?: number | null;
  source_trust?: number | null;
  reasoning_confidence?: number | null;
  composite?: number | null;
  explanation?: string;
  created_at: string;
}
interface ShapeViolation {
  id: string; run_id?: string; shape_set_id?: string;
  focus_node: string; result_path?: string;
  source_constraint?: string;
  severity: string; enforcement: string;
  created_at?: string;
}

type Tab = "audit" | "merges" | "credentials" | "cost" | "lineage" | "confidence" | "shapes" | "assurance" | "identity";

export function Governance() {
  const ws = useApp((s) => s.workspaceId);
  const [tab, setTab] = useState<Tab>("audit");

  const tabs: Array<{ id: Tab; label: string; help: string }> = [
    { id: "audit",       label: "Audit",         help: "Every write to the workspace — actor, action, resource." },
    { id: "merges",      label: "Merge review",  help: "Entity-resolution proposals awaiting human approval. Approval authorises a non-destructive SAME_AS merge." },
    { id: "credentials", label: "Credentials",   help: "LLM / data-provider credentials (alias of IAM endpoint)." },
    { id: "cost",        label: "Cost",          help: "Per-run LLM spend: tokens, USD, latency, provider, model." },
    { id: "lineage",     label: "Lineage",       help: "Provenance edges: document → chunk → entity → answer." },
    { id: "confidence",  label: "Confidence",    help: "Per-target trust: retrieval / ontology / source / reasoning / composite." },
    { id: "shapes",      label: "Shape violations", help: "SHACL constraint failures with severity & enforcement." },
    { id: "assurance",   label: "Assurance",      help: "Measured latency envelopes, retention controls, legal hold, and tamper-evident exports." },
    { id: "identity",    label: "Identity",       help: "OIDC sign-in, group-to-workspace roles, and SCIM provisioning credentials." },
  ];
  const active = tabs.find((t) => t.id === tab)!;

  return (
    <>
      <PageHeader title="Governance"
                  subtitle="Every governance signal in one place."
                  help={
                    <>
                      <p>Read-only view of the audit trail, per-run cost, provenance lineage, confidence scores, and SHACL shape violations — the evidence behind every answer and action.</p>
                    </>
                  }
                  learnMore={{ to: "/howto", hash: "governance", label: "Audit, lineage & ABAC" }} />

      <div className="mb-3 flex flex-wrap gap-1 border-b border-border">
        {tabs.map((t) => (
          <button key={t.id}
                  onClick={() => setTab(t.id)}
                  className={
                    "px-3 py-1.5 text-xs transition-colors " +
                    (tab === t.id
                      ? "border-b-2 border-accent text-fg"
                      : "text-muted hover:text-fg")
                  }>
            {t.label}
          </button>
        ))}
      </div>
      <p className="mb-3 text-[11px] text-muted">{active.help}</p>

      {tab === "audit" && (
        <PaginatedTable<Audit>
          endpoint="/governance/audit/"
          baseParams={{ workspace_id: ws ?? undefined }}
          searchPlaceholder="Search by action / resource / actor…"
          cols={[
            { header: "When",     cell: (a) => fmtDate(a.created_at) },
            { header: "Actor",    cell: (a) => <code className="font-mono text-xs">{shortId(a.actor_id ?? a.actor)}</code> },
            { header: "Action",   cell: (a) => <code className="font-mono text-xs">{a.action}</code> },
            { header: "Resource", cell: (a) => (
                <code className="font-mono text-xs">
                  {a.resource_kind ?? a.resource}{a.resource_id ? `:${shortId(a.resource_id)}` : ""}
                </code>
              ) },
          ]}
        />
      )}

      {tab === "merges" && <MergeReview ws={ws} />}

      {tab === "credentials" && (
        <PaginatedTable<Cred>
          endpoint="/governance/credentials/"
          baseParams={{ workspace: ws ?? undefined }}
          searchPlaceholder="Search by provider or alias…"
          cols={[
            { header: "Provider", cell: (c) => <code className="font-mono text-xs">{c.provider}</code> },
            { header: "Alias",    cell: (c) => c.alias },
            { header: "Status",   cell: (c) => <StatusPill status={c.status} /> },
            { header: "ID",       cell: (c) => <code className="font-mono text-xs">{shortId(c.id)}</code> },
          ]}
        />
      )}

      {tab === "cost" && (
        <PaginatedTable<CostEvent>
          endpoint="/governance/cost/"
          baseParams={{ workspace_id: ws ?? undefined }}
          searchPlaceholder="Search by provider / model / actor…"
          cols={[
            { header: "When",     cell: (c) => fmtDate(c.created_at) },
            { header: "Provider", cell: (c) => <code className="font-mono text-xs">{c.provider}</code> },
            { header: "Model",    cell: (c) => <code className="font-mono text-xs">{c.model}</code> },
            { header: "Tokens (in/out)", cell: (c) => (
                <span className="font-mono text-xs">
                  {c.input_tokens.toLocaleString()} / {c.output_tokens.toLocaleString()}
                </span>
              ) },
            { header: "Cost (USD)", cell: (c) => (
                <span className="font-mono text-xs">
                  ${Number(c.cost_usd ?? 0).toFixed(4)}
                </span>
              ) },
            { header: "Latency (ms)", cell: (c) => (
                <span className="font-mono text-xs">{c.latency_ms ?? "—"}</span>
              ) },
            { header: "Run", cell: (c) => (
                c.run_id
                  ? <code className="font-mono text-xs">{shortId(c.run_id)}</code>
                  : <span className="text-muted">—</span>
              ) },
          ]}
        />
      )}

      {tab === "lineage" && (
        <PaginatedTable<LineageEdge>
          endpoint="/governance/lineage/"
          baseParams={{ workspace_id: ws ?? undefined }}
          searchPlaceholder="Search by op / kind…"
          cols={[
            { header: "When", cell: (e) => fmtDate(e.created_at) },
            { header: "Op",   cell: (e) => <code className="font-mono text-xs">{e.op}</code> },
            { header: "Source", cell: (e) => (
                <span className="font-mono text-xs">
                  {e.src_kind}:<span className="text-muted">{shortId(e.src_id)}</span>
                </span>
              ) },
            { header: "→ Destination", cell: (e) => (
                <span className="font-mono text-xs">
                  {e.dst_kind}:<span className="text-muted">{shortId(e.dst_id)}</span>
                </span>
              ) },
            { header: "Run", cell: (e) => (
                e.run_id
                  ? <code className="font-mono text-xs">{shortId(e.run_id)}</code>
                  : <span className="text-muted">—</span>
              ) },
          ]}
        />
      )}

      {tab === "confidence" && (
        <PaginatedTable<ConfidenceScore>
          endpoint="/governance/confidence/"
          baseParams={{ workspace_id: ws ?? undefined }}
          searchPlaceholder="Search by target kind…"
          cols={[
            { header: "When", cell: (c) => fmtDate(c.created_at) },
            { header: "Target", cell: (c) => (
                <span className="font-mono text-xs">
                  {c.target_kind}:<span className="text-muted">{shortId(c.target_id)}</span>
                </span>
              ) },
            { header: "Composite", cell: (c) => <ScoreBar value={c.composite} /> },
            { header: "Retrieval", cell: (c) => <ScoreNum value={c.retrieval_confidence} /> },
            { header: "Ontology",  cell: (c) => <ScoreNum value={c.ontology_confidence} /> },
            { header: "Source trust", cell: (c) => <ScoreNum value={c.source_trust} /> },
            { header: "Reasoning", cell: (c) => <ScoreNum value={c.reasoning_confidence} /> },
          ]}
        />
      )}

      {tab === "shapes" && (
        <PaginatedTable<ShapeViolation>
          endpoint="/governance/shape-violations/"
          baseParams={{ workspace_id: ws ?? undefined }}
          searchPlaceholder="Search by focus node / path…"
          statusOptions={[
            { label: "Info",      value: "info"      },
            { label: "Warning",   value: "warning"   },
            { label: "Violation", value: "violation" },
          ]}
          cols={[
            { header: "When", cell: (v) => fmtDate(v.created_at) },
            { header: "Severity",    cell: (v) => <StatusPill status={severityFor(v.severity)} /> },
            { header: "Enforcement", cell: (v) => <code className="font-mono text-xs">{v.enforcement}</code> },
            { header: "Focus node",  cell: (v) => <code className="font-mono text-xs">{shortId(v.focus_node)}</code> },
            { header: "Path",        cell: (v) => <code className="font-mono text-xs">{v.result_path || "—"}</code> },
            { header: "Constraint",  cell: (v) => <code className="font-mono text-xs">{v.source_constraint || "—"}</code> },
            { header: "Run", cell: (v) => (
                v.run_id
                  ? <code className="font-mono text-xs">{shortId(v.run_id)}</code>
                  : <span className="text-muted">—</span>
              ) },
          ]}
        />
      )}
      {tab === "assurance" && <AssurancePanel ws={ws} />}
      {tab === "identity" && <IdentityPanel ws={ws} />}
    </>
  );
}

interface PerformanceSummary {
  window_hours: number; sample_count: number; p50_ms: number | null;
  p95_ms: number | null; p99_ms: number | null; contractual_sla: boolean;
}
interface RetentionPolicy {
  id: string; data_class: string; retention_days: number; legal_hold: boolean;
  worm_destination: string;
}
interface AssuranceRun {
  id: string; control: string; status: string; report: Record<string, unknown>; created_at: string;
}

function AssurancePanel({ ws }: { ws: string | null }) {
  const qc = useQueryClient();
  const workspaces = useApp((state) => state.workspaces);
  const [dataClass, setDataClass] = useState("audit");
  const [days, setDays] = useState(730);
  const [legalHold, setLegalHold] = useState(false);
  const [destination, setDestination] = useState("");
  const [restoreTarget, setRestoreTarget] = useState("");
  const performance = useQuery<PerformanceSummary>({
    queryKey: ["performance-assurance", ws],
    queryFn: () => rawFetch(`/governance/assurance/performance/?workspace=${ws}&hours=24`),
    enabled: !!ws,
  });
  const policies = useQuery<{ results: RetentionPolicy[] }>({
    queryKey: ["retention-policies", ws],
    queryFn: () => rawFetch(`/governance/retention-policies/?workspace_id=${ws}&page_size=100`),
    enabled: !!ws,
  });
  const create = useMutation({
    mutationFn: () => rawFetch("/governance/retention-policies/", {
      method: "POST",
      body: JSON.stringify({ workspace_id: ws, data_class: dataClass, retention_days: days, legal_hold: legalHold, worm_destination: destination }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["retention-policies", ws] }),
  });
  const runs = useQuery<{ results: AssuranceRun[] }>({
    queryKey: ["assurance-runs", ws],
    queryFn: () => rawFetch(`/governance/assurance-runs/?workspace_id=${ws}&page_size=50`),
    enabled: !!ws,
  });
  const executeCheck = useMutation({
    mutationFn: (control: "tenant-isolation" | "exit-restore") => rawFetch(`/governance/assurance-runs/${control}/`, {
      method: "POST",
      body: JSON.stringify({ workspace: ws, target_workspace: restoreTarget || undefined }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assurance-runs", ws] }),
  });
  if (!ws) return <EmptyState title="Select a workspace first." />;
  const summary = performance.data;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {[["Samples", summary?.sample_count], ["p50", summary?.p50_ms], ["p95", summary?.p95_ms], ["p99", summary?.p99_ms]].map(([label, value]) => (
          <div key={String(label)} className="card">
            <div className="text-xs uppercase text-muted">{label}</div>
            <div className="mt-1 font-mono text-xl">{value ?? "—"}{label !== "Samples" && value != null ? " ms" : ""}</div>
          </div>
        ))}
      </div>
      <div className="rounded border border-warn/40 bg-warn/5 px-3 py-2 text-xs text-warn">
        Observed 24-hour measurements, not a contractual SLA. Capacity commitments require a customer-specific load test and deployment envelope.
      </div>
      <div className="card">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium"><ShieldCheck size={15} /> Retention policy</div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
          <select className="input" value={dataClass} onChange={(event) => setDataClass(event.target.value)}>
            {['audit', 'lineage', 'cost', 'confidence', 'shape'].map((value) => <option key={value}>{value}</option>)}
          </select>
          <input className="input" type="number" min={1} value={days} onChange={(event) => setDays(Number(event.target.value))} />
          <input className="input" placeholder="Customer WORM destination URI" value={destination} onChange={(event) => setDestination(event.target.value)} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={legalHold} onChange={(event) => setLegalHold(event.target.checked)} /> Legal hold</label>
        </div>
        <button className="btn-primary mt-3" disabled={create.isPending || days < 1} onClick={() => create.mutate()}>Save policy</button>
        {create.isError && <div className="mt-2"><ErrorBox error={create.error} /></div>}
      </div>
      {(policies.data?.results ?? []).map((policy) => (
        <div key={policy.id} className="card flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">{policy.data_class} · {policy.retention_days} days</div>
            <div className="text-xs text-muted">{policy.legal_hold ? "Legal hold: purge disabled" : "Purge eligibility after verified export"} · sink {policy.worm_destination || "not configured"}</div>
          </div>
          <button className="btn inline-flex items-center gap-1 text-xs" onClick={() => downloadBlob(`/governance/retention-policies/${policy.id}/export/`, `${policy.data_class}.ndjson`)}>
            <Download size={13} /> Export evidence
          </button>
        </div>
      ))}
      <div className="card">
        <div className="mb-3 text-sm font-medium">Executable assurance checks</div>
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn inline-flex items-center gap-1 text-xs" disabled={executeCheck.isPending} onClick={() => executeCheck.mutate("tenant-isolation")}>
            <ShieldCheck size={13} /> Run tenant isolation checks
          </button>
          <select className="input max-w-[260px] text-xs" value={restoreTarget} onChange={(event) => setRestoreTarget(event.target.value)}>
            <option value="">Restore target workspace</option>
            {workspaces.filter((item) => item.id !== ws).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <button className="btn text-xs" disabled={!restoreTarget || executeCheck.isPending} onClick={() => executeCheck.mutate("exit-restore")}>Run signed exit/restore drill</button>
        </div>
        <p className="mt-2 text-[11px] text-muted">Isolation checks are internal automated evidence, not an independent pentest. Exit drills use the signed ORB promotion path and require valid environment transitions.</p>
        {executeCheck.isError && <div className="mt-2"><ErrorBox error={executeCheck.error} /></div>}
      </div>
      {(runs.data?.results ?? []).map((run) => (
        <div key={run.id} className="card flex items-start justify-between gap-3">
          <div><div className="text-sm font-medium">{run.control.replaceAll("_", " ")}</div><div className="mt-1 text-xs text-muted">{fmtDate(run.created_at)} · {JSON.stringify(run.report)}</div></div>
          <StatusPill status={run.status} />
        </div>
      ))}
      {(performance.isError || policies.isError) && <ErrorBox error={performance.error ?? policies.error} />}
    </div>
  );
}

interface IdentityProvider {
  id: string; organization: string; issuer: string; client_id: string;
  groups_claim: string; enabled: boolean; jit_provisioning: boolean;
}
interface IdentityMapping { id: string; provider: string; group: string; workspace: string; role: string }

function IdentityPanel({ ws }: { ws: string | null }) {
  const qc = useQueryClient();
  const workspaces = useApp((state) => state.workspaces);
  const selected = workspaces.find((item) => item.id === ws);
  const organization = selected?.organization;
  const [issuer, setIssuer] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [group, setGroup] = useState("");
  const [role, setRole] = useState("viewer");
  const [scimToken, setScimToken] = useState("");
  const providers = useQuery<{ results: IdentityProvider[] }>({
    queryKey: ["identity-providers", organization],
    queryFn: () => rawFetch("/iam/identity-providers/?page_size=20", undefined, "json", false),
    enabled: !!organization,
  });
  const provider = providers.data?.results?.find((item) => item.organization === organization);
  const mappings = useQuery<{ results: IdentityMapping[] }>({
    queryKey: ["identity-mappings", provider?.id],
    queryFn: () => rawFetch(`/iam/identity-group-mappings/?provider=${provider?.id}&page_size=100`, undefined, "json", false),
    enabled: !!provider,
  });
  const saveProvider = useMutation({
    mutationFn: () => rawFetch(
      provider ? `/iam/identity-providers/${provider.id}/` : "/iam/identity-providers/",
      {
        method: provider ? "PATCH" : "POST",
        body: JSON.stringify({
          organization, issuer: issuer || provider?.issuer, client_id: clientId || provider?.client_id,
          ...(clientSecret ? { client_secret: clientSecret } : {}),
          groups_claim: provider?.groups_claim ?? "groups", enabled: true, jit_provisioning: true,
        }),
      }, "json", false,
    ),
    onSuccess: () => { setClientSecret(""); qc.invalidateQueries({ queryKey: ["identity-providers", organization] }); },
  });
  const testProvider = useMutation({
    mutationFn: () => rawFetch(`/iam/identity-providers/${provider?.id}/test/`, { method: "POST" }, "json", false),
  });
  const addMapping = useMutation({
    mutationFn: () => rawFetch("/iam/identity-group-mappings/", {
      method: "POST", body: JSON.stringify({ provider: provider?.id, group, workspace: ws, role }),
    }, "json", false),
    onSuccess: () => { setGroup(""); qc.invalidateQueries({ queryKey: ["identity-mappings", provider?.id] }); },
  });
  const issueScim = useMutation<{ token: string }>({
    mutationFn: () => rawFetch("/iam/scim-tokens/", {
      method: "POST", body: JSON.stringify({ provider: provider?.id, label: "Directory" }),
    }, "json", false),
    onSuccess: (data) => setScimToken(data.token),
  });
  if (!ws || !organization) return <EmptyState title="Select a workspace first." />;
  const error = providers.error ?? mappings.error ?? saveProvider.error ?? testProvider.error ?? addMapping.error ?? issueScim.error;
  return (
    <div className="space-y-4">
      <div className="card">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium"><ShieldCheck size={15} /> OpenID Connect</div>
        <div className="grid gap-2 md:grid-cols-3">
          <input className="input" placeholder={provider?.issuer ?? "https://idp.example.com"} value={issuer} onChange={(event) => setIssuer(event.target.value)} />
          <input className="input" placeholder={provider?.client_id ?? "Client ID"} value={clientId} onChange={(event) => setClientId(event.target.value)} />
          <input className="input" type="password" placeholder={provider ? "Leave blank to keep secret" : "Client secret"} value={clientSecret} onChange={(event) => setClientSecret(event.target.value)} />
        </div>
        <div className="mt-3 flex gap-2">
          <button className="btn-primary" disabled={saveProvider.isPending || (!provider && (!issuer || !clientId || !clientSecret))} onClick={() => saveProvider.mutate()}>Save provider</button>
          <button className="btn" disabled={!provider || testProvider.isPending} onClick={() => testProvider.mutate()}>Test discovery</button>
          {testProvider.isSuccess && <StatusPill status="passed" />}
        </div>
        <p className="mt-2 text-[11px] text-muted">Authorization code flow uses PKCE, nonce, state, JWKS signature verification, and verified email claims. Client secrets never return from the API.</p>
      </div>
      <div className="card">
        <div className="mb-3 text-sm font-medium">Group mapping</div>
        <div className="flex flex-wrap gap-2">
          <input className="input min-w-[220px] flex-1" placeholder="IdP group" value={group} onChange={(event) => setGroup(event.target.value)} />
          <select className="input" value={role} onChange={(event) => setRole(event.target.value)}>
            {['viewer', 'editor', 'admin', 'owner'].map((value) => <option key={value}>{value}</option>)}
          </select>
          <button className="btn" disabled={!provider || !group || addMapping.isPending} onClick={() => addMapping.mutate()}>Map to selected workspace</button>
        </div>
        <div className="mt-3 space-y-1 text-xs">
          {(mappings.data?.results ?? []).map((mapping) => <div key={mapping.id} className="flex justify-between border-t border-border py-2"><code>{mapping.group}</code><span>{mapping.role}</span></div>)}
        </div>
      </div>
      <div className="card">
        <div className="mb-2 text-sm font-medium">SCIM 2.0 Users</div>
        <button className="btn" disabled={!provider || issueScim.isPending} onClick={() => issueScim.mutate()}>Issue provisioning token</button>
        {scimToken && <div className="mt-3 rounded border border-warn/40 bg-warn/5 p-3"><div className="text-xs text-warn">Shown once. Store it in the identity provider now.</div><code className="mt-1 block break-all text-xs">{scimToken}</code></div>}
        <p className="mt-2 text-[11px] text-muted">Endpoint: /api/v1/iam/scim/v2/Users. Supports exact userName filters, create, active-state patching, and mapped groups. SCIM Groups and bulk operations are not supported.</p>
      </div>
      {error && <ErrorBox error={error} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ScoreNum({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined) return <span className="text-muted">—</span>;
  return <span className="font-mono text-xs">{Number(value).toFixed(2)}</span>;
}

function ScoreBar({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined) return <span className="text-muted">—</span>;
  const v = Math.max(0, Math.min(1, Number(value)));
  // Red → amber → green based on score.
  const hue = Math.round(v * 120);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded bg-border/40">
        <div className="h-full"
             style={{ width: `${v * 100}%`, backgroundColor: `hsl(${hue} 70% 45%)` }} />
      </div>
      <span className="font-mono text-xs">{v.toFixed(2)}</span>
    </div>
  );
}

/** StatusPill maps "violation" → bad, "warning" → warn, "info" → neutral. */
function severityFor(sev: string): string {
  const s = sev.toLowerCase();
  if (s === "violation") return "failed";
  if (s === "warning") return "running";
  return "draft"; // neutral pill
}

/* ------------------------------------------------------------------ */

interface ResolutionProposal {
  id: string; source_iri: string; target_iri: string; entity_type?: string;
  score: number; method?: string; status: string;
  evidence?: Record<string, unknown>; created_at: string;
}

function MergeReview({ ws }: { ws: string | null }) {
  const qc = useQueryClient();
  const proposals = useQuery<{ results: ResolutionProposal[] }>({
    queryKey: ["resolution-proposals", ws],
    queryFn: () => rawFetch(`/governance/resolution-proposals/?status=proposed&page_size=50${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!ws,
  });

  const decide = (verb: "approve" | "reject") => useMutation({
    mutationFn: (id: string) => rawFetch(`/governance/resolution-proposals/${id}/${verb}/`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resolution-proposals", ws] }),
  });
  const approve = decide("approve");
  const reject = decide("reject");

  if (!ws) return <EmptyState title="Select a workspace first." />;
  if (proposals.isError) return <ErrorBox error={proposals.error} />;
  const rows = proposals.data?.results ?? [];
  if (proposals.isLoading) return <div className="text-sm text-muted">Loading…</div>;
  if (rows.length === 0)
    return <EmptyState title="No merge proposals pending." hint="The enrich.resolve processor queues sub-threshold matches here for human review." />;

  return (
    <div className="card overflow-x-auto p-0">
      <table className="min-w-full text-sm">
        <thead className="bg-border/30 text-left text-xs uppercase tracking-wide text-muted">
          <tr>
            <th className="px-3 py-2 font-medium">Candidate pair</th>
            <th className="px-3 py-2 font-medium">Type</th>
            <th className="px-3 py-2 font-medium">Score</th>
            <th className="px-3 py-2 font-medium">Method</th>
            <th className="px-3 py-2 font-medium">Decision</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((p) => {
            const busy = (m: typeof approve) => m.isPending && m.variables === p.id;
            return (
              <tr key={p.id}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 font-mono text-xs">
                    <span className="truncate" title={p.source_iri}>{p.source_iri}</span>
                    <GitMerge size={13} className="shrink-0 text-accent" />
                    <span className="truncate text-muted" title={p.target_iri}>{p.target_iri}</span>
                  </div>
                </td>
                <td className="px-3 py-2"><code className="text-xs">{p.entity_type || "—"}</code></td>
                <td className="px-3 py-2 font-mono text-xs">{p.score.toFixed(3)}</td>
                <td className="px-3 py-2"><code className="text-xs text-muted">{p.method || "—"}</code></td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button className="rounded-md border border-border p-1.5 text-good hover:bg-border/40"
                            title="Approve merge" disabled={busy(approve)} onClick={() => approve.mutate(p.id)}>
                      {busy(approve) ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    </button>
                    <button className="rounded-md border border-border p-1.5 text-bad hover:bg-border/40"
                            title="Reject merge" disabled={busy(reject)} onClick={() => reject.mutate(p.id)}>
                      {busy(reject) ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
